import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, TrendingUp, CheckCircle2, GraduationCap, Users, Play } from "lucide-react";
import Icon from "@/components/Icon";
import VideoModal from "@/components/VideoModal";
import { TRADES, ROADMAP_DETAIL, IMAGES, VIDEOS, TRADE_VIDEOS } from "@/lib/data";

export default function Trades() {
  const [trade, setTrade] = useState(null);
  const [levelIdx, setLevelIdx] = useState(0);
  const [videoSrc, setVideoSrc] = useState(null);

  const openTrade = (t) => { setTrade(t); setLevelIdx(0); };
  const d = ROADMAP_DETAIL.default;

  return (
    <section id="trades" className="relative py-24 sm:py-32 border-b border-white/10 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]">
        <img src={IMAGES.repairCrane} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <div className="font-mono-tech text-[11px] tracking-[0.25em] text-[#FF5E1E] mb-3">[ SEC—LEVEL-UP — CAREER ADVANCEMENT ]</div>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
            What trade do you want to build?
          </h2>
          <p className="mt-4 max-w-2xl text-[#8FA3B8]">
            Firewatch is the <span className="text-white font-semibold">entry point — not the end</span>. Pick a trade and follow its interactive career roadmap from entry to lead. Click any level to see the detail.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TRADES.map((t, i) => (
            <motion.button
              key={t.id}
              data-testid={`trade-card-${t.id}`}
              onClick={() => openTrade(t)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 3) * 0.06, duration: 0.5 }}
              className="group text-left bg-[#10161F]/90 backdrop-blur border border-white/10 hover:border-[#FF5E1E]/60 p-5 transition-colors"
            >
              <div className="flex items-start justify-between">
                <span className="h-11 w-11 grid place-items-center bg-[#182230] text-[#FF5E1E] group-hover:bg-[#FF5E1E] group-hover:text-[#0B0F14] transition-colors">
                  <Icon name={t.icon} size={22} />
                </span>
                {t.partner && <span className="font-mono-tech text-[9px] tracking-[0.15em] text-[#0099FF] border border-[#0099FF]/40 px-1.5 py-0.5">PARTNER</span>}
              </div>
              <h3 className="mt-4 font-display font-bold uppercase text-lg leading-tight">{t.name}</h3>
              <div className="mt-2 flex items-center gap-2 text-xs text-[#8FA3B8]">
                <TrendingUp size={13} className="text-[#10B981]" /> {t.wage}
              </div>
              <div className="mt-4">
                <div className="flex justify-between font-mono-tech text-[9px] tracking-[0.15em] text-[#8FA3B8] mb-1">
                  <span>SHIPYARD DEMAND</span><span>{t.demand}%</span>
                </div>
                <div className="h-1 bg-[#0B0F14]">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${t.demand}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.2 }}
                    className="h-full bg-gradient-to-r from-[#FF5E1E] to-[#FFB800]"
                  />
                </div>
              </div>
              <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-mono-tech tracking-wide text-white/70 group-hover:text-[#FF5E1E] transition-colors">
                VIEW ROADMAP <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!trade} onOpenChange={(o) => !o && setTrade(null)}>
        <DialogContent className="max-w-3xl bg-[#10161F] border-white/10 text-[#F4F6F8] rounded-none p-0 gap-0 max-h-[90vh] overflow-y-auto [&>button]:hidden">
          {trade && (
            <>
              <DialogHeader className="p-6 border-b border-white/10 sticky top-0 bg-[#10161F] z-10">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="h-12 w-12 grid place-items-center bg-[#FF5E1E] text-[#0B0F14]"><Icon name={trade.icon} size={24} /></span>
                    <div>
                      <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">CAREER ROADMAP · {trade.wage}</div>
                      <DialogTitle className="font-display font-black uppercase text-2xl tracking-tight">{trade.name}</DialogTitle>
                    </div>
                  </div>
                  <button data-testid="trade-modal-close-button" onClick={() => setTrade(null)} className="text-[#8FA3B8] hover:text-white p-1"><X size={20} /></button>
                </div>
              </DialogHeader>

              <div className="grid md:grid-cols-5 gap-0">
                {/* Roadmap ladder */}
                <div className="md:col-span-2 p-6 border-b md:border-b-0 md:border-r border-white/10">
                  <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E] mb-4">PROGRESSION — CLICK A LEVEL</div>
                  <div className="space-y-1">
                    {trade.roadmap.map((lvl, i) => (
                      <button
                        key={i}
                        data-testid={`roadmap-level-${trade.id}-${i}`}
                        onClick={() => setLevelIdx(i)}
                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${
                          levelIdx === i ? "border-[#FF5E1E] bg-[#FF5E1E]/10 text-white" : "border-white/10 text-[#8FA3B8] hover:border-white/40 hover:text-white"
                        }`}
                      >
                        <span className="font-mono-tech text-[10px] w-5">{String(i + 1).padStart(2, "0")}</span>
                        <span className="text-sm font-medium">{lvl}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Level detail */}
                <div className="md:col-span-3 p-6 space-y-5">
                  <div>
                    <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">LEVEL {String(levelIdx + 1).padStart(2, "0")}</div>
                    <h4 className="font-display font-black uppercase text-xl mt-1">{trade.roadmap[levelIdx]}</h4>
                  </div>
                  <DetailRow icon={Users} label="What the job does" value={d.does} />
                  <DetailRow icon={CheckCircle2} label="Skills required" value={d.skills} />
                  <DetailRow icon={GraduationCap} label="Training required" value={d.training} />
                  <DetailRow icon={CheckCircle2} label="Credentials" value={d.credentials} />
                  <DetailRow icon={TrendingUp} label="Experience & next level" value={d.experience} />

                  <button
                    data-testid="trade-watch-video-button"
                    onClick={() => setVideoSrc(TRADE_VIDEOS[trade.id] || VIDEOS.structural)}
                    className="w-full flex items-center gap-3 bg-[#0B0F14] border border-white/10 hover:border-[#FF5E1E]/60 p-3 transition-colors group"
                  >
                    <span className="h-9 w-9 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] group-hover:scale-105 transition-transform"><Play size={16} /></span>
                    <span className="font-display font-bold uppercase text-sm">Watch a day in this trade</span>
                  </button>

                  <div className="flex flex-wrap gap-3 pt-1">
                    <Button asChild className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase text-xs h-10">
                      <a href="#apply" onClick={() => setTrade(null)}>Start This Path</a>
                    </Button>
                    <Button asChild variant="outline" className="rounded-none border-white/20 bg-transparent hover:bg-white/10 text-white font-display font-bold uppercase text-xs h-10">
                      <a href="#jobs" onClick={() => setTrade(null)}>See Current Jobs</a>
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#8FA3B8]">* Qualification/certification titles are shown only once actually earned and verified.</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <VideoModal open={!!videoSrc} onClose={() => setVideoSrc(null)} src={videoSrc} title={trade ? `${trade.name} — On The Job` : "On The Job"} />
    </section>
  );
}

function DetailRow({ icon: I, label, value }) {
  return (
    <div className="flex gap-3">
      <I size={16} className="text-[#FF5E1E] shrink-0 mt-0.5" />
      <div>
        <div className="font-mono-tech text-[9px] tracking-[0.2em] text-[#8FA3B8]">{label}</div>
        <p className="text-sm text-[#B8C4D0] leading-relaxed mt-0.5">{value}</p>
      </div>
    </div>
  );
}
